import { connect, useSelector } from 'react-redux'
import { func, object, oneOf, string } from 'prop-types'
import { featureSection, features, painelFeatures } from './constants'
import * as actions from './actions'
import { makeSelectFeatureInfo } from './selectors'
import { useEffect } from 'react'
import { makeSelectProjectId } from '../PlanSelection/selectors'
import { createStructuredSelector } from 'reselect'
import { useRouteMatch } from 'react-router-dom'
import { StyledFlag } from './styledComponents'
import { causeOptions } from './labels'
import { isEmpty } from 'lodash'
import { getOrganizationId } from '../../react-query/authentication/query/use-me-query'

const FeatureAvailability = ({
  key,
  render,
  featureSlug,
  selectedProjectId,
  featureSection,
  setUnavailableFeatureSlug,
  getProjectFeatureInfo,
  getOrganisationFeatureInfo,
  isPinDetails = false,
  ...props
}) => {
  const { params } = useRouteMatch()
  const organisationId = getOrganizationId()

  const { projectId: routeProjectId } = params

  const projectId =
    featureSlug === 'REPORT_TEMPLATE'
      ? undefined
      : featureSlug === 'PIN_FIELDS'
        ? routeProjectId || selectedProjectId
        : selectedProjectId || routeProjectId

  const isProjectRoute =
    params[0] === '/project' || params[0] === '/' || isEmpty(params)

  const selectFeatureInfo = makeSelectFeatureInfo(featureSlug, projectId)
  const {
    featureInfo,
    featureInfoLoading: loading,
    featureInfoError: error
  } = useSelector(selectFeatureInfo)

  const currentTier = featureInfo?.currentTier?.name?.toLowerCase()
  const requiredTier = featureInfo?.requiredTier?.name?.toLowerCase()
  const restriction = featureInfo?.restriction

  useEffect(() => {
    if (!featureSlug) {
      return
    }
    if (projectId && !isProjectRoute) {
      getProjectFeatureInfo(featureSlug, projectId)
    } else if (organisationId) {
      getOrganisationFeatureInfo(featureSlug)
    }
  }, [projectId, organisationId, featureSlug, featureSection])

  const checkPinDetailsEdgeCase = () => {
    const isPinFieldsFeature = featureSlug === 'PIN_FIELDS'
    const hasRestriction = !isEmpty(restriction)
    const isExternalProjectTooLow =
      restriction?.cause === causeOptions.TO_LOW &&
      restriction?.externalProject === true

    return (
      isPinFieldsFeature &&
      hasRestriction &&
      isExternalProjectTooLow &&
      isPinDetails
    )
  }

  const isEnabled = () => {
    return featureInfo?.available === true || checkPinDetailsEdgeCase()
  }

  const isDisabled = () => {
    return featureSlug && !isEnabled()
  }

  const handleClick = (e) => {
    if (isDisabled() && !loading) {
      setUnavailableFeatureSlug(
        featureSlug,
        featureSection,
        requiredTier,
        restriction,
        currentTier,
        requiredTier
      )
    }

    // We don't want to disable click event when feature is enabled or it displays
    // message in a panel instead of dialog.
    if (!isDisabled() || painelFeatures.includes(featureSlug)) {
      return
    }

    // For other features we want to disable click, because they are not available for user.
    e.preventDefault()
    e.stopPropagation()
  }

  if (!isDisabled()) {
    return render({
      isDisabled,
      isEnabled,
      requiredTier,
      currentTier,
      loading,
      error,
      restriction,
      featureSection
    })
  }

  return (
    <StyledFlag
      key={key}
      onClickCapture={handleClick}
      className="featureAvailability"
      disabled={isDisabled() || loading}
      {...props}
    >
      {render({
        isDisabled,
        isEnabled,
        requiredTier,
        currentTier,
        loading,
        error,
        restriction,
        featureSection
      })}
    </StyledFlag>
  )
}

FeatureAvailability.propTypes = {
  key: string,
  render: func,
  featureSlug: oneOf(features),
  featureSection: oneOf(featureSection),
  setUnavailableFeatureSlug: func,
  featureInfo: object,
  selectedProjectId: string
}

const mapStateToProps = createStructuredSelector({
  selectedProjectId: makeSelectProjectId()
})

function mapDispatchToProps(dispatch) {
  return {
    getProjectFeatureInfo: (featureSlug, projectId) =>
      dispatch(actions.getFeatureInfoByProject(featureSlug, projectId)),
    getOrganisationFeatureInfo: (featureSlug) =>
      dispatch(actions.getFeatureInfoByOrganisation(featureSlug)),
    setUnavailableFeatureSlug: (...props) =>
      dispatch(actions.setUnavailableFeatureSlug(...props))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FeatureAvailability)
